import { motion, AnimatePresence } from 'framer-motion';
import { useAppStore } from '../stores/appStore';
import './BackpackModal.css';

interface BackpackModalProps {
    isOpen: boolean;
    onClose: () => void;
    onOpenStore: () => void;
}

const ITEM_DEFS = [
    {
        id: 'compass',
        name: 'Compass',
        icon: '🧭',
        description: 'Reveals the next hidden letter of the secret word in Classic mode.',
        mode: 'Classic',
    },
    {
        id: 'echo',
        name: 'SOS Echo',
        icon: '📡',
        description: 'Reveals an AI-generated hint for a target card in Flip mode.',
        mode: 'Flip',
    },
];

export default function BackpackModal({ isOpen, onClose, onOpenStore }: BackpackModalProps) {
    const user = useAppStore(state => state.user);
    const inventory = user?.inventory ?? {};

    const totalItems = ITEM_DEFS.reduce((sum, item) => sum + (inventory[item.id] ?? 0), 0);

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="backpack-overlay" onClick={onClose}>
                    <motion.div
                        className="backpack-modal"
                        initial={{ opacity: 0, scale: 0.95, y: 12 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 12 }}
                        transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="backpack-header">
                            <div className="backpack-title-group">
                                <span className="backpack-title-icon">🎒</span>
                                <h2 className="backpack-title">Backpack</h2>
                                {totalItems > 0 && (
                                    <span className="backpack-total-badge">{totalItems} item{totalItems !== 1 ? 's' : ''}</span>
                                )}
                            </div>
                            <button className="backpack-close-btn" onClick={onClose}>×</button>
                        </div>

                        <div className="backpack-body">
                            {totalItems === 0 ? (
                                <div className="backpack-empty">
                                    <span className="backpack-empty-icon">🎒</span>
                                    <p className="backpack-empty-title">Your backpack is empty</p>
                                    <p className="backpack-empty-sub">Visit the Store to pick up some items.</p>
                                </div>
                            ) : (
                                <div className="backpack-items">
                                    {ITEM_DEFS.map((item, i) => {
                                        const qty = inventory[item.id] ?? 0;
                                        return (
                                            <motion.div
                                                key={item.id}
                                                className={`backpack-item ${qty === 0 ? 'empty' : ''}`}
                                                initial={{ opacity: 0, y: 8 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: i * 0.06 }}
                                            >
                                                <div className="backpack-item-icon">{item.icon}</div>
                                                <div className="backpack-item-body">
                                                    <div className="backpack-item-top">
                                                        <span className="backpack-item-name">{item.name}</span>
                                                        <span className="backpack-item-mode">{item.mode}</span>
                                                    </div>
                                                    <p className="backpack-item-desc">{item.description}</p>
                                                </div>
                                                <div className={`backpack-item-qty ${qty === 0 ? 'zero' : ''}`}>
                                                    ×{qty}
                                                </div>
                                            </motion.div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        <div className="backpack-footer">
                            <button
                                className="backpack-store-btn"
                                onClick={() => { onClose(); onOpenStore(); }}
                            >
                                🏪 Go to Store
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
}
